import { useDialog } from 'contexts/dialog';
import { useViewport } from 'contexts/viewport';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, FormLabel, Paper, Stack, styled, Switch, Text } from 'shell/components';

const GradingSection = memo(
    ({ isEditable, form, setForm, errors, setGradingSettingsDialog, openVideoGradingSettings }) => {
        const { setDialog, closeDialog } = useDialog();
        const { device } = useViewport();
        const { t } = useTranslation('assignment');

        const getText = () => {
            if (Boolean(form.gradingCustomContext) || Boolean(form.gradingSettings)) {
                return errors.gradingSettings
                    ? t('custom-settings-with-error', 'Custom settings {{error}}', {
                          error: errors.gradingSettings,
                      })
                    : t('custom-settings', 'Custom settings');
            }

            return t('system-default', 'System default');
        };

        return (
            <Stack sx={{ width: '100%' }} spacing={1}>
                <Text variant={'p1'}>{t('grading-section.title', 'Grading')}</Text>
                <CustomPaper variant={'outlined'}>
                    <Stack spacing={4}>
                        <CustomPaper variant={'outlined'}>
                            <Stack spacing={3}>
                                <Stack
                                    alignItems={device === 'mobile' ? 'flex-start' : 'center'}
                                    direction={'row'}
                                    justifyContent={'space-between'}
                                >
                                    <Stack
                                        alignItems={device === 'mobile' ? 'flex-start' : 'center'}
                                        direction={device === 'mobile' ? 'column-reverse' : 'row'}
                                        spacing={device === 'mobile' ? 2 : 4}
                                    >
                                        <Text variant={'p1'}>
                                            {t('grading-section.grading-settings', 'Grading Settings')}
                                        </Text>
                                        {/* The video doesn't exist yet, so this button is hidden for now */}
                                        {/* <Button
                                        startIcon={<Icon kind={'video'} />}
                                        variant={'borderless'}
                                        onClick={() => openVideoGradingSettings()}
                                    >
                                        Watch a Tutorial
                                    </Button> */}
                                    </Stack>
                                    <Button
                                        disabled={!isEditable || !form.aiGradingEnabled}
                                        variant={'borderless'}
                                        onClick={() => setGradingSettingsDialog({ open: true })}
                                    >
                                        {t('advanced-settings', 'Advanced Settings')}
                                    </Button>
                                </Stack>
                                <Text color={errors.gradingSettings ? 'error' : 'light'}>{getText()}</Text>
                            </Stack>
                        </CustomPaper>
                        <Stack alignItems={'center'} direction={'row'} spacing={4}>
                            <FormLabel id={'grading-switch'}>
                                <Text variant={'p1'}>
                                    {t('grading-section.grading-suggestions', 'Grading Suggestions')}
                                </Text>
                            </FormLabel>
                            <Switch
                                aria-labelledby={'grading-switch'}
                                checked={form.aiGradingEnabled}
                                disabled={!isEditable}
                                onChange={(_, value) => {
                                    if (form.feedbackSettings.structure === 'templated') {
                                        setDialog({
                                            open: true,
                                            message: t(
                                                'dialog.disable-grading-reset-warning.title',
                                                'Disabling Grading will Reset Feedback to Default Settings'
                                            ),
                                            description: t(
                                                'dialog.disable-grading-reset-warning.description',
                                                'Smart Feedback Templates are currently selected, which requires grading to be enabled. If you disable grading, feedback will be changed to the system default. Do you want to continue?'
                                            ),
                                            note: '',
                                            variant: 'destructive',
                                            actionButtonText: t('common:controls.continue', 'Continue'),
                                            cancelButtonText: t('common:controls.cancel', 'Cancel'),
                                            onAction: () => {
                                                closeDialog();
                                                setForm((current) => ({
                                                    ...current,
                                                    aiGradingEnabled: value,
                                                    feedbackSettings: {
                                                        ...current.feedbackSettings,
                                                        structure: 'system',
                                                    },
                                                }));
                                            },
                                            onCancel: () => closeDialog(),
                                            onClose: () => closeDialog(),
                                        });
                                    } else {
                                        setForm((current) => ({ ...current, aiGradingEnabled: value }));
                                    }
                                }}
                            />
                        </Stack>
                        <Text>
                            {t(
                                'grading-section.assignment-description',
                                'TimelyGrader leverages assignment details to provide instructors with personalized grading suggestions for student’s final submissions. If disabled, grading must be done manually.'
                            )}
                        </Text>
                    </Stack>
                </CustomPaper>
            </Stack>
        );
    }
);

const CustomPaper = styled(Paper)(({ theme }) => {
    return {
        padding: theme.spacing(4),
    };
});

export default GradingSection;
